import {cx as addStyles, css} from '@emotion/css';
import {ResaleType} from '@remoov/functional';
import {theme} from '@remoov/remoov-components';
import React, {HTMLAttributes, TextareaHTMLAttributes} from 'react';
import clockCounterwise from './clockcounterwise.svg';

const row = css({display: 'flex', flexDirection: 'row'});
export const ItemsBoardContainer = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({
        width: '100%',
        maxWidth: '1200px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        flexWrap: 'nowrap',
      }),
      className,
    )}
    {...rest}
  />
);
export const ItemsBoardHeader = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      row,
      css({
        alignItems: 'center',
        justifyContent: 'flex-start',
        background: 'rgba(247, 148, 29, 0.05)',
        width: '100%',
        [theme.mediaMaxWidth.large]: {display: 'none'},
      }),
      className,
    )}
    {...rest}
  />
);
export const ItemsBoardLeftContainer = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      row,
      css({
        width: '100%',
        maxWidth: '406px',
        alignItems: 'center',
        justifyContent: 'flex-start',
      }),
      className,
    )}
    {...rest}
  />
);
export const ItemsBoardRightContainer = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      row,
      css({
        width: '100%',
        paddingLeft: theme.spacings.large,
        alignItems: 'center',
        justifyContent: 'flex-start',
      }),
      className,
    )}
    {...rest}
  />
);
export const ItemsBoardRow = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      row,
      css({
        width: '100%',
        minWidth: '135px',
        alignItems: 'center',
        justifyContent: 'flex-start',
      }),
      className,
    )}
    {...rest}
  />
);
export const ItemsBoardRowWithActionIcon = ({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) => (
  <ItemsBoardRow
    className={addStyles(
      css({
        cursor: 'pointer',
        img: {width: '16px', height: '16px', cursor: 'pointer', margin: '0 4px'},
      }),
      className,
    )}
    {...rest}
  />
);
export const ItemsBoardColumn = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({
        width: '100%',
        minWidth: '135px',
        display: 'flex',
        flexDirection: 'column',
      }),
      className,
    )}
    {...rest}
  />
);
export const textBaseStyle = css({
  color: theme.colors.darkGray,
  fontFamily: theme.font.family,
  fontSize: theme.font.sizes.small,
  fontWeight: theme.font.normal,
  textAlign: 'left',
});
export const ItemsBoardTitle = ({className, ...rest}: HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={addStyles(textBaseStyle, css({fontWeight: theme.font.bold}), className)}
    {...rest}
  />
);
export const InfoText = ({className, ...rest}: HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={addStyles(textBaseStyle, css({fontWeight: theme.font.light}), className)}
    {...rest}
  />
);
export const BoldInfoText = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <InfoText className={addStyles(css({fontWeight: theme.font.bold}, className))} {...rest} />
);
export const ResaleTypeChangeBadge = ({resaleType}: {resaleType: ResaleType}) => (
  <span
    className={css({
      display: 'inline-flex',
      padding: '4px',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '4px',
      marginTop: '4px',
      borderRadius: '2px',
      border: `1px solid ${theme.colors.lightBlue}`,
      background: '#F7F7FF',
      color: theme.colors.darkBlue,
      fontFamily: theme.font.family,
      fontSize: theme.font.sizes.xsmall,
      fontWeight: theme.font.bold,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      alignSelf: 'flex-start',
    })}
  >
    <img
      src={clockCounterwise}
      alt=''
      className={css({
        width: '14px',
        height: '14px',
      })}
    />
    {resaleType}
  </span>
);
export const CustomerItemStatusWithBadge = ({
  status,
  resaleTypeBadge,
}: {
  status: Maybe<string>;
  resaleTypeBadge: Maybe<ResaleType>;
}) => (
  <ItemsBoardColumn className={css({alignItems: 'flex-start'})}>
    {status && <InfoText>{status}</InfoText>}
    {resaleTypeBadge && <ResaleTypeChangeBadge resaleType={resaleTypeBadge} />}
  </ItemsBoardColumn>
);
export const ImageBox = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({
        minWidth: '114px',
        maxWidth: '114px',
        height: '90%',
        cursor: 'pointer',
        img: {width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px'},
      }),
      className,
    )}
    {...rest}
  />
);
export const ItemsBoardDivisionLine = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({
        width: '100%',
        maxWidth: '1100px',
        height: '1px',
        background: theme.colors.lightBlue,
        padding: '0 50px',
      }),
      className,
    )}
    {...rest}
  />
);
export const HideOnLargeScreen = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({display: 'none', [theme.mediaMaxWidth.large]: {display: 'block'}}),
      className,
    )}
    {...rest}
  />
);
export const HideOnSmallScreen = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({display: 'block', width: '100%', [theme.mediaMaxWidth.large]: {display: 'none'}}),
      className,
    )}
    {...rest}
  />
);
export const DashboardCard = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({
        width: '100%',
        background: theme.colors.white,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'flex-start',
        padding: theme.spacings.large,
        [theme.mediaMaxWidth.medium]: {padding: `${theme.spacings.medium} ${theme.spacings.small}`},
      }),
      className,
    )}
    {...rest}
  />
);
export const DashboardCardTitle = ({className, ...rest}: HTMLAttributes<HTMLHeadingElement>) => (
  <h1
    className={addStyles(
      textBaseStyle,
      css({
        fontSize: theme.font.sizes.medium,
        fontWeight: theme.font.bold,
        marginBottom: theme.spacings.medium,
      }),
      className,
    )}
    {...rest}
  />
);
export const ItemDetailsOpenContainer = ({className, ...rest}: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({
        display: 'flex',
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        [theme.mediaMaxWidth.large]: {flexDirection: 'column'},
      }),
      className,
    )}
    {...rest}
  />
);
export const ItemDetails = (args: TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea
    className={css({
      width: '100%',
      maxWidth: '720px',
      height: '78px',
      border: `1px solid ${theme.colors.darkBlue}`,
      padding: `${theme.spacings.xxsmall} ${theme.spacings.xsmall}`,
      color: theme.colors.darkBlue,
      fontSize: theme.font.sizes.small,
      fontFamily: theme.font.family,
      fontWeight: theme.font.light,
      resize: 'none',
      '&::placeholder': {
        opacity: 0.7,
        color: theme.colors.darkBlue,
        fontFamily: theme.font.family,
        fontSize: theme.font.sizes.small,
      },
      [theme.mediaMaxWidth.large_huge]: {padding: theme.spacings.xxsmall},
      '@media (max-width: 500px)': {maxWidth: '350px'},
    })}
    {...args}
  />
);
export const ItemDetailsContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'flex-start',
      width: '100%',
      marginTop: theme.spacings.medium,
      gap: theme.spacings.medium,
      paddingRight: '50px',
      [theme.mediaMaxWidth.large]: {
        width: '80%',
        margin: `${theme.spacings.xxsmall} auto 0`,
        flexDirection: 'column',
        gap: theme.spacings.xsmall,
        padding: theme.spacings.xsmall,
      },
      [theme.mediaMaxWidth.small]: {width: '100%'},
    })}
    {...args}
  />
);
